import { computed, watch, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useHead } from '@vueuse/head';

const BASE_URL = 'https://chds.com.au';
const DEFAULT_IMAGE = `${BASE_URL}/og-default.jpg`;

/**
 * SEO Composable for Vue components.
 * Provides Open Graph, Twitter Cards, canonical URLs, and standard meta tags.
 *
 * @param {Object} options
 * @param {string|ComputedRef} options.title
 * @param {string|ComputedRef} options.description
 * @param {string|ComputedRef} options.keywords
 * @param {string|ComputedRef} options.image
 * @param {string} options.type - OG type (default: 'website')
 * @param {boolean} options.noindex - add noindex,nofollow robots meta
 * @param {string} options.path - custom canonical path (defaults to current route)
 * @param {Object|Array|ComputedRef} options.jsonLd - one or more JSON-LD objects rendered as
 *        <script type="application/ld+json"> (structured data for Google). May be a plain object,
 *        an array of objects, or a computed/getter returning either.
 */
export function useSEO(options = {}) {
	const route = useRoute();

	const unwrap = (v, fallback) => {
		if (typeof v === 'function') return v.value || fallback;
		if (v && typeof v === 'object' && 'value' in v) return v.value || fallback;
		return v || fallback;
	};

	// Language twins: /x (en-AU) and /zh/x (zh-Hans). The URL decides the language of the
	// head; a page that passes a fixed `path` still gets the /zh prefix on the Chinese twin.
	const isZh = computed(() => route.path === '/zh' || route.path.startsWith('/zh/'));
	// `path` and `noindex` may also be computed refs (a page that changes identity, e.g. the
	// weekly menu rendered inside a partner's co-branded page).
	const fixedPath = computed(() => unwrap(options.path, ''));
	const noindex = computed(() => (typeof options.noindex === 'function' || (options.noindex && typeof options.noindex === 'object' && 'value' in options.noindex)) ? !!options.noindex.value : !!options.noindex);
	const currentPath = computed(() => {
		const p = fixedPath.value || route.path;
		if (fixedPath.value && isZh.value && !p.startsWith('/zh')) return p === '/' ? '/zh' : `/zh${p}`;
		return p;
	});
	const canonicalUrl = computed(() => `${BASE_URL}${currentPath.value}`);
	const enUrl = computed(() => `${BASE_URL}${isZh.value ? (currentPath.value === '/zh' ? '/' : currentPath.value.replace(/^\/zh/, '')) : currentPath.value}`);
	const zhUrl = computed(() => (isZh.value ? canonicalUrl.value : `${BASE_URL}${currentPath.value === '/' ? '/zh' : '/zh' + currentPath.value}`));
	const title = computed(() => (isZh.value && options.titleZh ? unwrap(options.titleZh, '') : '') || unwrap(options.title, 'Chi Hun Da Su'));
	const description = computed(() => (isZh.value && options.descriptionZh ? unwrap(options.descriptionZh, '') : '') || unwrap(options.description, 'Order authentic Chinese food from Chi Hun Da Su'));
	const keywords = computed(() => unwrap(options.keywords, ''));
	const image = computed(() => unwrap(options.image, DEFAULT_IMAGE));
	const ogType = options.type || 'website';

	const metaTags = computed(() => {
		const tags = [{ name: 'description', content: description.value }];
		if (keywords.value) tags.push({ name: 'keywords', content: keywords.value });
		if (noindex.value) tags.push({ name: 'robots', content: 'noindex, nofollow' });
		tags.push(
			{ property: 'og:title', content: title.value },
			{ property: 'og:description', content: description.value },
			{ property: 'og:image', content: image.value },
			{ property: 'og:url', content: canonicalUrl.value },
			{ property: 'og:type', content: ogType },
			{ property: 'og:site_name', content: 'CHDS 吃荤搭素' },
			{ property: 'og:locale', content: isZh.value ? 'zh_CN' : 'en_AU' },
			{ name: 'twitter:card', content: 'summary_large_image' },
			{ name: 'twitter:title', content: title.value },
			{ name: 'twitter:description', content: description.value },
			{ name: 'twitter:image', content: image.value },
		);
		return tags;
	});

	// Structured data (JSON-LD). Accepts an object, an array, or a computed/getter of either.
	const scriptTags = computed(() => {
		if (!options.jsonLd) return [];
		const raw = typeof options.jsonLd === 'function' ? options.jsonLd.value : options.jsonLd;
		const blocks = (Array.isArray(raw) ? raw : [raw]).filter(Boolean);
		return blocks.map((data, i) => ({
			type: 'application/ld+json',
			// textContent is XSS-safe (serialised as text, not parsed as HTML). Our data is static.
			textContent: JSON.stringify(data),
			key: `chds-ld-${currentPath.value}-${i}`,
		}));
	});

	const buildHead = () => {
		const head = {
			title: title.value,
			meta: metaTags.value,
			link: noindex.value ? [{ rel: 'canonical', href: canonicalUrl.value }] : [
				{ rel: 'canonical', href: canonicalUrl.value },
				{ rel: 'alternate', hreflang: 'en-AU', href: enUrl.value },
				{ rel: 'alternate', hreflang: 'zh-Hans', href: zhUrl.value },
				{ rel: 'alternate', hreflang: 'x-default', href: enUrl.value },
			],
			htmlAttrs: { lang: isZh.value ? 'zh-Hans' : 'en-AU' },
		};
		if (scriptTags.value.length) head.script = scriptTags.value;
		return head;
	};

	const headConfig = ref(buildHead());
	watch([title, metaTags, canonicalUrl, scriptTags, isZh, noindex], () => { headConfig.value = buildHead(); }, { immediate: false });
	useHead(headConfig);

	return { title, description, keywords, image, canonicalUrl };
}
